import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import { FooterComponents } from "../FooterComponent";
import { getToken, getUserId } from '../../utils/auth';
import { ClipboardCheck, Star, Save, ArrowLeft, User, GraduationCap, AlertCircle, CheckCircle } from "lucide-react";

export default function ApplicationAssessment() {
  const { applicationId, programId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [application, setApplication] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [markings, setMarkings] = useState({});
  const [comments, setComments] = useState({});
  const [existingMarkings, setExistingMarkings] = useState([]);
  const [error, setError] = useState(null);

  const evaluatorId = getUserId();

  useEffect(() => {
    fetchApplicationDetails();
    fetchCriteria();
    fetchExistingMarkings();
  }, [applicationId, programId]);

  const fetchApplicationDetails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}hec/abroad-scholarship-application/get-by-id?id=${applicationId}`,
        {
          headers: {
            Authorization: getToken() ? `Bearer ${getToken()}` : undefined,
          },
        }
      );
      setApplication(response.data);
    } catch (err) {
      setError("Failed to fetch application details");
      toast.error("Failed to load application details");
    }
  };

  const fetchCriteria = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}hec/abroad-selection-criteria/get-by-education-program?educationProgramId=${programId}`,
        {
          headers: {
            Authorization: getToken() ? `Bearer ${getToken()}` : undefined,
          },
        }
      );
      setCriteria(response.data || []);
    } catch (err) {
      setError("Failed to fetch selection criteria");
      toast.error("Failed to load selection criteria");
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingMarkings = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}hec/abroadSelectionMarking/application?applicationId=${applicationId}`,
        {
          headers: {
            Authorization: getToken() ? `Bearer ${getToken()}` : undefined,
          },
        }
      );
      const myMarkings = response.data.filter(m => m.evaluatorId === evaluatorId);
      setExistingMarkings(myMarkings);
      
      // Pre-fill markings and comments
      const marksObj = {};
      const commentsObj = {};
      myMarkings.forEach(marking => {
        marksObj[marking.criteriaId] = marking.marksObtained;
        commentsObj[marking.criteriaId] = marking.comment || "";
      });
      setMarkings(marksObj);
      setComments(commentsObj);
    } catch (err) {
      console.error("Failed to fetch existing markings");
    }
  };

  const handleMarkingChange = (criteriaId, value) => {
    setMarkings(prev => ({
      ...prev,
      [criteriaId]: parseFloat(value)
    }));
  };

  const handleCommentChange = (criteriaId, value) => {
    setComments(prev => ({
      ...prev,
      [criteriaId]: value
    }));
  };

  const handleSaveMarkings = async () => {
    // Validate that all criteria have marks
    const unfilledCriteria = criteria.filter(criterion => !markings[criterion.id] || markings[criterion.id] === 0);
    
    if (unfilledCriteria.length > 0) {
      toast.error("Please fill in marks for all criteria before saving.", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
      return;
    }

    setSaving(true);
    try {
      const markingsToSave = criteria.map(criterion => ({
       
        
        applicationId: applicationId,
        programId: programId,
        evaluatorId: evaluatorId,
        criteriaId: criterion.id,
        marksObtained: markings[criterion.id] || 0,
        weightObtained: ((markings[criterion.id] || 0) * (criterion.weightAllocated || 0)) / 100,
        comment: comments[criterion.id] || "",
        isFinal: false
      }));

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}hec/abroadSelectionMarking/saveMultiple`,
        markingsToSave,
        {
          headers: {
            Authorization: getToken() ? `Bearer ${getToken()}` : undefined,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        toast.success("Markings saved successfully!", {
          position: "top-center",
          autoClose: 3000,
          theme: "colored",
        });
        // Clear inputs
        setMarkings({});
        setComments({});
        // Refresh existing markings
        await fetchExistingMarkings();
      }
    } catch (err) {
      toast.error("Failed to save markings. Please try again.", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    } finally {
      setSaving(false);
    }
  };

  const getTotalMarks = () => {
    // Calculate weighted sum: sum of (marks obtained × weight / 100) for each criterion
    // Formula: (marks1 * weight1)/100 + (marks2 * weight2)/100 + ...
    return criteria.reduce((sum, criterion) => {
      const marksObtained = markings[criterion.id] || 0;
      const weight = criterion.weightAllocated || 0;
      return sum + (marksObtained * weight / 100);
    }, 0);
  };



  const getPercentage = () => {
    const total = getTotalMarks();
    return total.toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBarComponent />
        <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
          <SidebarComponent />
        </aside>
        <div className="pt-6 sm:ml-64">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-16 h-16 bg-hec-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <ClipboardCheck className="w-8 h-8 text-hec-600" />
              </div>
              <p className="text-hec-600 text-lg">Loading assessment...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopBarComponent />
        <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
          <SidebarComponent />
        </aside>
        <div className="pt-6 sm:ml-64">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 text-lg">{error}</p>
              <button
                onClick={() => navigate(-1)}
                className="mt-4 px-6 py-2 bg-hec-700 text-white rounded-lg hover:bg-hec-600"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBarComponent />
      <aside className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200">
        <SidebarComponent />
      </aside>

      <div className="pt-6 sm:ml-64">
        <div className="p-6 min-h-screen">
          <div className="py-8 mt-10">
            {/* Header */}
            <div className="mb-8">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-hec-700 hover:text-hec-600 mb-4 font-medium"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Applications
              </button>
              
              <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-hec-600 to-hec-700 rounded-2xl flex items-center justify-center shadow-lg">
                      <ClipboardCheck className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-hec-700">Application Assessment</h1>
                      <p className="text-hec-600 mt-1">Evaluate and mark the application based on selection criteria</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Total Score</p>
                      <p className="text-2xl font-bold text-hec-700">
                        {getTotalMarks().toFixed(2)} / 100
                      </p>
                      <p className="text-sm text-gray-500">{getPercentage()}%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Application Details */}
            {application && (
              <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6 mb-6">
                <h2 className="text-xl font-semibold text-hec-700 mb-4 flex items-center">
                  <User className="w-6 h-6 mr-2" />
                  Applicant Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-semibold text-gray-900">
                      {application.user?.firstName} {application.user?.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-semibold text-gray-900">{application.user?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">National ID</p>
                    <p className="font-semibold text-gray-900">{application.user?.nationalId}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Apply Level</p>
                    <p className="font-semibold text-gray-900">{application.applyLevel}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Priority Course</p>
                    <p className="font-semibold text-gray-900">
                      {application.priorityCourse?.courseName || "N/A"} ({application.priorityCourse?.priorityLevel || "N/A"})
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Academic Merit</p>
                    <p className="font-semibold text-gray-900">
                      {application.academicMerit?.letterGrade || "N/A"} ({application.academicMerit?.marks || "N/A"})
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Assessment Criteria */}
            <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6 mb-6">
              <h2 className="text-xl font-semibold text-hec-700 mb-6 flex items-center">
                <Star className="w-6 h-6 mr-2" />
                Assessment Criteria
              </h2>

              {criteria.length === 0 ? (
                <div className="text-center py-12">
                  <GraduationCap className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No selection criteria available for this program.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {criteria.map((criterion, index) => (
                    <div key={criterion.id} className="border border-gray-200 rounded-lg p-6 hover:border-hec-300 transition-colors">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="bg-hec-100 text-hec-700 px-3 py-1 rounded-full text-sm font-semibold">
                              Criterion {index + 1}
                            </span>
                            <span className="text-sm text-gray-600">
                              Weight: {criterion.weightAllocated} marks
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {criterion.criteriaName}
                          </h3>
                          {criterion.evaluationGuideline && (
                            <p className="text-gray-600 text-sm mb-4">{criterion.evaluationGuideline}</p>
                          )}
                        </div>
                      </div>

                      {/* Evaluation Scales */}
                      {criterion.evaluationScales && criterion.evaluationScales.length > 0 && (
                        <div className="mb-4 bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm font-semibold text-gray-700 mb-2">Evaluation Scale ({criterion.evaluationScaleTemplate}):</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {criterion.evaluationScales.map((scale) => (
                              <div key={scale.id} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                                <span className="text-sm text-gray-700">{scale.scaleGrade}</span>
                                <span className="text-sm font-semibold text-hec-700">
                                  {scale.marksAwarded}% ({scale.minScore}-{scale.maxScore})
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Marks Input */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Marks Obtained (0-100%) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            required
                            value={markings[criterion.id] || ""}
                            onChange={(e) => handleMarkingChange(criterion.id, e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-hec-500"
                            placeholder="Enter marks (0-100)"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Weighted contribution: {((markings[criterion.id] || 0) * criterion.weightAllocated / 100).toFixed(2)} / {criterion.weightAllocated}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Comments (Optional)
                          </label>
                          <textarea
                            value={comments[criterion.id] || ""}
                            onChange={(e) => handleCommentChange(criterion.id, e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-hec-500"
                            rows="2"
                            placeholder="Add your comments..."
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Save Button */}
            {criteria.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      {existingMarkings.length > 0 
                        ? "You have already assessed this application. Saving will update your previous assessment."
                        : "Your assessment will be saved and can be edited later."}
                    </p>
                  </div>
                  <button
                    onClick={handleSaveMarkings}
                    disabled={saving || criteria.length === 0}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-hec-600 to-hec-700 text-white rounded-lg hover:from-hec-700 hover:to-hec-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {saving ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        <span>Save Assessment</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <FooterComponents />
      </div>
    </div>
  );
}
